import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  CircularProgress,
  Divider,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { CacheKey } from "../../types/cache-keys";
import {
  useAddGtmSolutionMutation,
  useUpdateGtmArchitectureMutation,
  useFetchGtmArchitectures,
  useFetchSingleGtmArchitecture,
} from "../../hooks/gtm-solution-hooks";
import ChildCrudPanel from "./Panels/ChildCrudPanel";
import ResourceRelationPanel from "./Panels/ResourceRelationPanel";
import TagManagementSection from "./Common/TagManagementSection";
import { GtmSolution } from "../../types/go-to-market";
import SnackableCourseButton from './SnackableCourse/SnackableCourseButton';
import SnackableCourseManager from './SnackableCourse/SnackableCourseManager';
import { validId } from "../../hooks/base-hooks";
import { useNavigate, useParams } from "react-router-dom";
import { ADMIN_URL_DATA_MANAGMENT } from "./PageStructure/admin-values";
import { useAdminPermissions } from "./Hooks/useAdminPermissions";
import NavHistoryTracker from "../../navigation/NavHistoryTracker";
import AdminConfirmDialog from "./Common/AdminConfirmDialog";
import GtmPageContentSection from "./GtmContent/GtmPageContentSection";

type Props = {
  readOnly?: boolean;
  canCreateChildren?: boolean;
  canUpdateChildren?: boolean;
  canDeleteChildren?: boolean;
};

const emptyForm = {
  name: '',
  type: '',
  description: '',
  url: '',
};

export default function GoToMarketEditorPage({
                                               readOnly = false,
                                               canCreateChildren = true,
                                               canUpdateChildren = true,
                                               canDeleteChildren = true,
                                             }: Props) {
  const { id } = useParams();
  const gtmId: string = id ?? 'new';
  const isNew = gtmId === 'new';
  const [courseManagerOpen, setCourseManagerOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const gtmNumericId = isNew ? null : Number(gtmId);
  const navigate = useNavigate();
  const { hasGtmScope } = useAdminPermissions();

  const { data: solutions, isLoading: isLoadingSolutions } = useFetchGtmArchitectures();
  const { data: singleSolution, isLoading: isLoadingSingleSolution } =
    useFetchSingleGtmArchitecture(gtmNumericId, {}, { enabled: !isNew && validId(gtmNumericId) });

  const gtmSolution = useMemo(() => {
    if (!isNew && gtmNumericId) {
      if (isLoadingSolutions && isLoadingSingleSolution) return null;
      if (singleSolution) return singleSolution;
      const found = solutions?.find(s => s.id === gtmNumericId);
      return found ?? null;
    }
    return null;
  }, [gtmNumericId, solutions, isLoadingSolutions, isNew, singleSolution, isLoadingSingleSolution]);

  const { mutateAsync: addGtmSolution, isPending: isAddingGtmSolution } = useAddGtmSolutionMutation();
  const { mutateAsync: updateGtmSolution, isPending: isUpdatingGtmSolution } = useUpdateGtmArchitectureMutation();

  const [form, setForm] = useState<any>(emptyForm);
  const [initialForm, setInitialForm] = useState<any>(emptyForm);

  useEffect(() => {
    if (gtmSolution && !isNew) {
      const nextForm = { ...emptyForm, ...gtmSolution };
      setForm(nextForm);
      setInitialForm(nextForm);
    } else if (isNew) {
      setForm(emptyForm);
      setInitialForm(emptyForm);
    }
  }, [gtmSolution, isNew]);

  const saving = isAddingGtmSolution || isUpdatingGtmSolution;
  const loading = isLoadingSolutions || isLoadingSingleSolution;

  const hasUnsavedChanges = useMemo(() => {
    return (
      form.name !== initialForm.name ||
      form.type !== initialForm.type ||
      form.description !== initialForm.description ||
      form.url !== initialForm.url
    );
  }, [form, initialForm]);

  const handleSave = async () => {
    if (readOnly) return;

    if (isNew) {
      const created: GtmSolution = await addGtmSolution(form);
      if (created?.id) {
        navigate(`${ADMIN_URL_DATA_MANAGMENT}/goToMarkets/${created.id}`);
      }
    } else if (gtmNumericId) {
      await updateGtmSolution({ ...form, id: gtmNumericId });
      setInitialForm(form);
    }
  };

  const resetAndClose = () => {
    setConfirmCloseOpen(false);
    navigate(`${ADMIN_URL_DATA_MANAGMENT}/goToMarkets`);
  };

  const handleClose = () => {
    if (readOnly || !hasUnsavedChanges) {
      resetAndClose();
      return;
    }
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    resetAndClose();
  };

  return (
    <Box className="my-6">
      {(isNew || !!gtmSolution) && (
        <NavHistoryTracker title={isNew ? 'New GTM Solution' : gtmSolution.name} />
      )}

      {!hasGtmScope ? (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No scope access for Go-To-Markets.
          </Typography>
        </Box>
      ) : (
        <>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Button startIcon={<ArrowBack />} onClick={handleClose}>
                Back
              </Button>
              <Typography variant="h5">
                {readOnly
                  ? 'View Go-To-Market'
                  : isNew
                    ? 'Create Go-To-Market'
                    : form?.name
                      ? `Go-To-Market: ${form.name}`
                      : `Go-To-Market #${gtmNumericId}`}
              </Typography>
            </Box>

            <Box display="flex" gap={2} alignItems="center">
              {(loading || saving) && <CircularProgress size={20} />}

              {!isNew && (
                <SnackableCourseButton
                  entityType="go_to_market"
                  entityId={gtmNumericId}
                  entityName={form.name}
                  disabled={isNew}
                />
              )}

              {!readOnly && (
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                  Save
                </Button>
              )}
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <ChildCrudPanel
                key={`gtm-${gtmNumericId}-components`}
                parentId={gtmNumericId}
                sectionType={CacheKey.GtmSolutionComponent}
                readOnly={readOnly}
                canCreate={canCreateChildren}
                canUpdate={canUpdateChildren}
                canDelete={canDeleteChildren}
              />

              <Box mt={2} />

              <ChildCrudPanel
                key={`gtm-${gtmNumericId}-usecases`}
                parentId={gtmNumericId}
                sectionType={CacheKey.GtmSolutionUseCase}
                readOnly={readOnly}
                canCreate={canCreateChildren}
                canUpdate={canUpdateChildren}
                canDelete={canDeleteChildren}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6">Attributes</Typography>
                <Divider sx={{ my: 1.5 }} />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Name"
                  value={form.name ?? ''}
                  onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, name: e.target.value }))}
                  disabled={readOnly}
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Type"
                  value={form.type ?? ''}
                  onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, type: e.target.value }))}
                  disabled={readOnly}
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="URL"
                  value={form.url ?? ''}
                  onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, url: e.target.value }))}
                  disabled={readOnly}
                />

                <TextField
                  fullWidth
                  margin="normal"
                  label="Description"
                  value={form.description ?? ''}
                  onChange={(e) => !readOnly && setForm((p: any) => ({ ...p, description: e.target.value }))}
                  multiline
                  minRows={3}
                  disabled={readOnly}
                />
              </Paper>

              <Box mt={2} />

              <Paper sx={{ p: 2 }}>
                <TagManagementSection
                  parentId={gtmNumericId}
                  parentType={CacheKey.GtmSolution}
                  disabled={!validId(gtmNumericId) || readOnly}
                />
              </Paper>

              <Box mt={2} />

              <Paper sx={{ p: 2 }}>
                <ResourceRelationPanel
                  joinEndpoint="db/go-to-market-to-resource"
                  joinCacheKey={CacheKey.GtmSolutionToResource}
                  parentField="go_to_market"
                  parentId={gtmNumericId}
                  title="Resources"
                  showNameColumn={true}
                  maxHeight={400}
                  invalidateKeys={[CacheKey.GtmSolution]}
                  readOnly={readOnly as any}
                />
              </Paper>
            </Grid>

            {/* The page copy: rows come from the detailed payload above, so this adds no fetch. */}
            <Grid item xs={12}>
              <GtmPageContentSection
                gtmId={validId(gtmNumericId) ? gtmNumericId : null}
                gtm={gtmSolution}
                loading={loading}
                readOnly={readOnly}
                canCreate={canCreateChildren}
                canUpdate={canUpdateChildren}
                canDelete={canDeleteChildren}
              />
            </Grid>
          </Grid>

          {courseManagerOpen && (
            <SnackableCourseManager
              open={courseManagerOpen}
              entityType="go_to_market"
              entityId={gtmNumericId}
              onClose={() => setCourseManagerOpen(false)}
            />
          )}
        </>
      )}

      <AdminConfirmDialog
        open={confirmCloseOpen}
        title="Discard Changes"
        message={
          <Box>
            <Typography variant="body1" gutterBottom>
              You have unsaved changes in this go-to-market editor.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              If you continue, your changes will be lost.
            </Typography>
          </Box>
        }
        confirmLabel="Discard Changes"
        cancelLabel="Continue Editing"
        confirmColor="warning"
        loading={saving}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </Box>
  );
}